/**
 * Session-private attachment upload client (#639).
 *
 * Posts to `POST /api/agent/sessions/:sessionID/attachments` — a
 * `multipart/form-data` upload into the tenant CAS as a session-private,
 * STAGED `session_attachments` row (never a drive `files` row, never
 * visible outside the session). See
 * `features/agent/api/routes/attachments.ts` for the full server-side gate
 * stack (session ownership, workspace write, cost limits, the fenced
 * commit).
 *
 * This is the composer's ONLY upload path for binary attachments as of
 * Task 3.4 — the prior `.temp/{sessionId}/` drive upload is gone.
 */
import { request, fetchRaw, type RequestIdentity } from '@papan/api-client'
import { toWireConflictStrategy } from '@papan/types'

export interface SessionAttachmentUploadResult {
  handle: string
  size: number
  deduped: boolean
}

/**
 * Upload one file into the session-private CAS store.
 *
 * `idempotencyKey` must be a value the caller controls and can reproduce on
 * retry (the server dedupes on it within the session) — callers should pass
 * a value generated once per attachment attempt, not regenerated per retry.
 * `source` must be one of the server's `ALLOWED_SOURCES` ('upload' | 'paste'
 * | 'drop'); anything else is coerced server-side to 'upload'.
 */
/** #1292: an upload's own time bound. `request()`'s default 30 s is a TOTAL
 *  bound, and since #1292 photos that overflow a message's inline budget take
 *  this path: an 8 MB photo needs ~32 s at 2 Mbit/s. Generous, but bounded so a
 *  dead connection still ends in an error chip rather than a spinner forever. */
export const SESSION_ATTACHMENT_UPLOAD_TIMEOUT_MS = 5 * 60_000

export async function uploadSessionAttachment(
  sessionId: string,
  workspaceId: string,
  file: File,
  /** `identity`: a pinned batch's (`batch.opts()`, #1316) — the upload is sent as it, and finishes. */
  options: { idempotencyKey: string; source: 'upload' | 'paste' | 'drop'; signal?: AbortSignal; identity?: RequestIdentity },
): Promise<SessionAttachmentUploadResult> {
  const formData = new FormData()
  formData.append('file', file, file.name)
  formData.append('idempotencyKey', options.idempotencyKey)
  formData.append('source', options.source)

  // Passing a signal opts out of request()'s default timeout, so this one
  // carries its own (cleared when the request settles) plus the caller's
  // cancel — #1292: removing the chip cancels the upload.
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(new DOMException('upload timed out', 'TimeoutError')), SESSION_ATTACHMENT_UPLOAD_TIMEOUT_MS)
  const onCancel = () => ctrl.abort(options.signal?.reason)
  if (options.signal?.aborted) onCancel()
  else options.signal?.addEventListener('abort', onCancel, { once: true })

  // No explicit Content-Type header — the browser sets the multipart
  // boundary itself. `request()`'s default headers never set one either,
  // so this rides the same fetch-based JSON-error-handling path as every
  // other mutation (credentials included, X-Tenant-Id).
  try {
    return await request<SessionAttachmentUploadResult>(
      `/api/agent/sessions/${sessionId}/attachments?workspaceId=${encodeURIComponent(workspaceId)}`,
      {
        method: 'POST',
        body: formData,
        signal: ctrl.signal,
        ...(options.identity ? { identity: options.identity } : {}),
      },
    )
  } finally {
    clearTimeout(timer)
    options.signal?.removeEventListener('abort', onCancel)
  }
}

/**
 * #1292 (D7): the user removed a chip whose upload had landed — delete the
 * server's row now, not after the staged-row sweep's grace window (P8: a write
 * nobody wants is undone at once). The server deletes it only while it is still
 * STAGED (never sent): a handle from an earlier message (an edit's chips) is
 * left alone, so this is safe to call for any composer chip. Best effort and
 * never awaited by the UI — a failure leaves the row to the sweep.
 */
export async function discardSessionAttachment(sessionId: string, workspaceId: string, handle: string): Promise<void> {
  await request<{ discarded: boolean }>(
    `/api/agent/sessions/${sessionId}/attachments/${encodeURIComponent(handle)}?workspaceId=${encodeURIComponent(workspaceId)}`,
    { method: 'DELETE' },
  )
}

/**
 * May the caller attach files in this project's chat? The server's own answer — the same `mayWriteWorkspace` the
 * upload route refuses with (#1053) — so the composer never offers what the route would refuse, or hides what it
 * would accept. Never derive this from a role.
 */
export async function fetchCanAttach(workspaceId: string): Promise<boolean> {
  const r = await request<{ canAttach: boolean }>(
    `/api/agent/attachments/can-attach?workspaceId=${encodeURIComponent(workspaceId)}`,
  )
  return r.canAttach === true
}

export interface SnapshotAttachmentUploadResult {
  handle: string
  size: number
  deduped: boolean
}

/**
 * Upload a `preview_screenshot` capture through the DIRECT binary channel
 * (#639 Task 4.2, spec §4.4) — `POST …/attachments/snapshot`, a SEPARATE
 * route from `uploadSessionAttachment` above. AG-UI caps a client tool
 * RESULT at 512 KB (agui.ts:143); a real screenshot's base64 routinely
 * exceeds that, so the bytes travel here directly and the AG-UI tool result
 * carries only the small `handle` this returns.
 *
 * Presents the dispatch-time ONE-USE capability minted by the worker at
 * `awaitClientTool` (apps/worker/src/processors/agent.ts) — NOT the same
 * gate as a user upload: no cost-limit check runs on this route (a snapshot
 * rides an already budget-admitted turn, §13.6), and no workspace-write
 * check either (a view-only user's agent may still snapshot, §4.4/§5). The
 * server derives the idempotency key from `toolCallId` itself — one
 * capability, one legitimate attempt — so there is no `idempotencyKey`
 * param here the way there is above.
 */
export async function uploadSnapshotAttachment(
  sessionId: string,
  workspaceId: string,
  blob: Blob,
  options: { runId: string; toolCallId: string; filename?: string },
): Promise<SnapshotAttachmentUploadResult> {
  const formData = new FormData()
  formData.append('file', blob, options.filename ?? 'screenshot.png')
  formData.append('runId', options.runId)
  formData.append('toolCallId', options.toolCallId)

  return request<SnapshotAttachmentUploadResult>(
    `/api/agent/sessions/${sessionId}/attachments/snapshot?workspaceId=${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      body: formData,
    },
  )
}

/**
 * Path for `GET /sessions/:sessionID/attachments/:handle` (#639, Task 2.5) —
 * the session-gated, live-checked binary read `AttachmentViewer` (Task 5.1)
 * fetches on demand. Exported separately from `fetchSessionAttachment` below
 * so a test can assert the exact URL a fetch went to without inspecting a
 * mocked `fetch` call's opaque `Request`.
 */
export function sessionAttachmentUrl(sessionId: string, workspaceId: string, handle: string): string {
  return `/api/agent/sessions/${sessionId}/attachments/${handle}?workspaceId=${encodeURIComponent(workspaceId)}`
}

/**
 * Fetch one session-private attachment's bytes for the in-chat viewer (#639,
 * Task 5.1). Every call is a fresh network round-trip — the endpoint
 * re-checks session ownership and sets `Cache-Control: private, no-store`
 * (attachments.ts) precisely so a revoked session/handle stops resolving
 * immediately. Do not memoize or cache this response; the caller
 * (`AttachmentViewer`) re-fetches on every open and revokes its object URL
 * on close.
 *
 * Uses `fetchRaw` (not `request`) because the response body is a binary blob,
 * not JSON — `fetchRaw` still attaches credentials + the default auth headers
 * (tenant id, org email) the endpoint's `assertSessionOwner` gate needs.
 */
export async function fetchSessionAttachment(sessionId: string, workspaceId: string, handle: string): Promise<Response> {
  return fetchRaw(sessionAttachmentUrl(sessionId, workspaceId, handle))
}

/**
 * `POST /sessions/:sessionID/attachments/:handle/save-to-drive` (#639, Task
 * 6.2) — the Save-to-drive DIALOG's save call, the user-triggered twin of
 * the `move_to_drive` LLM tool (renamed from `save_to_drive` — #662 Task 5).
 * Runs AS THE REQUEST'S OWN HUMAN (the
 * endpoint reads the request's auth, never an agent identity) — a
 * DELIBERATE privacy downgrade: the file becomes visible to everyone with
 * access at the destination. See `features/agent/api/routes/attachments.ts`
 * and `features/agent/core/save-to-drive.ts`.
 *
 * `destParentId` is an already-resolved drive folder id (what the dialog's
 * folder picker returns) — `null`/omitted means the tenant drive root.
 * `destFileName` overrides the attachment's own filename; omit to keep it.
 * `messageId`/`messagePartId` identify the source chip's message part
 * (#662) — when `messagePartId` is supplied, a successful save re-points
 * that part at the new drive file (turning the copy into a move); omit both
 * for today's non-consuming copy (e.g. a destination-only save with no
 * single source chip in view).
 *
 * `conflictMode` (#662 conflict warning) — omit or `'suffix'` for today's
 * silent-suffix behavior (every caller except the dialog). The dialog
 * passes `'ask'`: on a real name collision the server returns 409
 * `NAME_CONFLICT` WITHOUT writing, and `@papan/api-client`'s `request()`
 * surfaces `existingName`/`wouldSaveAs` on the thrown error (same
 * "known extra field" convention it already uses for `tenantName` /
 * `retryAfterSec`) — so the caller reads them off the caught error rather
 * than this function needing its own conflict-result return type.
 *
 * `resolution` (#662) — the human's pick from the drive's shared
 * ConflictModal, sent on the RE-call after a `NAME_CONFLICT`: `'keepBoth'`
 * (numeric-suffixed copy) or `'replace'` (a new version of the existing file —
 * same file, its history kept).
 * `'skip'` never reaches here — the dialog just cancels.
 */
export interface SaveToDriveResult {
  fileId: string
  parentId: string
  /** The name the file actually landed under — numeric-suffixed on a
   *  collision, never a silent overwrite. */
  fileName: string
  /** The saved file's `/papan/...` VFS path (#662) — what a caller stores on
   *  a moved chip so it re-opens the live drive file. */
  path: string
  size: number
  originalFilename: string
}

/**
 * Move an INLINE attachment -- a pasted image, whose bytes live in the
 * message part with no CAS handle -- into the drive, in ONE request.
 *
 * WHY NOT THE HANDLE ENDPOINT BELOW. That one needs a handle, so this dialog
 * used to upload the same bytes a second time to get one. An upload is
 * CHARGED, and the inline row it is replacing is still live while it happens,
 * so the same screenshot was counted twice for the span of the move and a
 * member at their chat-storage limit was refused the one action the Storage
 * page tells them frees that quota. Here the server reads the bytes out of
 * the part itself and credits exactly the row it is about to drop, so the
 * member's figure never double-counts.
 *
 * Addressed by the message PART id, which is all a chip knows. A JUST-SENT
 * chip's part id is still the optimistic one, so pass `messageId` and
 * `filename` too: the server falls back to those, and refuses rather than
 * guess if two files in that message share the name.
 *
 * Same two-call conflict protocol as the handle endpoint: call with
 * `conflictMode: 'ask'` and no `resolution` and a collision comes back as
 * `NAME_CONFLICT` having written nothing; the human's answer goes on the
 * re-call.
 */
export async function requestMoveInlineAttachmentToDrive(
  sessionId: string,
  workspaceId: string,
  partId: string,
  dest: {
    destParentId?: string | null
    messageId?: string
    filename?: string
    conflictMode?: 'suffix' | 'ask'
    resolution?: 'keepBoth' | 'replace'
  },
  /** `identity`: a pinned batch's (`batch.opts()`, #1316) — the move is sent as it, and finishes. */
  opts: { identity?: RequestIdentity } = {},
): Promise<Pick<SaveToDriveResult, 'fileId' | 'fileName' | 'path'>> {
  return request<Pick<SaveToDriveResult, 'fileId' | 'fileName' | 'path'>>(
    `/api/agent/sessions/${sessionId}/chat-storage/parts/${encodeURIComponent(partId)}/move-to-drive?workspaceId=${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      // Replace goes out under its wire name, exactly as below.
      body: JSON.stringify(dest.resolution ? { ...dest, resolution: toWireConflictStrategy(dest.resolution) } : dest),
      ...(opts.identity ? { identity: opts.identity } : {}),
    },
  )
}

export async function requestSaveSessionAttachmentToDrive(
  sessionId: string,
  workspaceId: string,
  handle: string,
  dest: { destParentId?: string | null; destFileName?: string; messageId?: string; messagePartId?: string; conflictMode?: 'suffix' | 'ask'; resolution?: 'keepBoth' | 'replace' },
  /** `identity`: a pinned batch's (`batch.opts()`, #1316) — the save is sent as it, and finishes. */
  opts: { identity?: RequestIdentity } = {},
): Promise<SaveToDriveResult> {
  return request<SaveToDriveResult>(
    `/api/agent/sessions/${sessionId}/attachments/${handle}/save-to-drive?workspaceId=${encodeURIComponent(workspaceId)}`,
    {
      method: 'POST',
      // Replace goes out under its wire name (`replaceVersion`): an old server never takes it as a
      // bin-and-create Replace (rolling deploy).
      body: JSON.stringify(dest.resolution ? { ...dest, resolution: toWireConflictStrategy(dest.resolution) } : dest),
      ...(opts.identity ? { identity: opts.identity } : {}),
    },
  )
}
